import { useAuthenticator } from '@aws-amplify/ui-react';
import { FetchBaseQueryError, QueryStatus } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { api } from '@/redux/api';
import { useAppDispatch } from '@/redux/hooks';
import { ApplicationUser } from '@/types/application-user';

type AuthStatus = ReturnType<typeof useAuthenticator>['authStatus'];

export type UseAuthStatusReturn = {
  isAuthenticated: boolean;
  cognitoAuthStatus: AuthStatus;
  userQueryStatus: QueryStatus;
  user: ApplicationUser | undefined;
};

export const useAuthStatus = (): UseAuthStatusReturn => {
  const dispatch = useAppDispatch();
  const { authStatus: cognitoAuthStatus, signOut } = useAuthenticator();

  const {
    currentData: user,
    status: userQueryStatus,
    error: userQueryError,
  } = api.useGetUserQuery(undefined, {
    skip: cognitoAuthStatus !== 'authenticated',
  });

  const typedUseQueryError: Partial<FetchBaseQueryError> = useMemo(
    () => (userQueryError as FetchBaseQueryError) ?? {},
    [userQueryError],
  );

  useEffect(() => {
    if (cognitoAuthStatus === 'authenticated') {
      if (
        userQueryStatus === QueryStatus.rejected &&
        typedUseQueryError.status
      ) {
        // TODO: remove once confirmed working
        // handle user account not found i.e. deactivated but might still have a valid access token form cognito
        if (typedUseQueryError.status === 401) {
          signOut();
        }
        // TODO: handle other errors i.e. show something wen wrong screen, try again etc
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoAuthStatus, typedUseQueryError.status, userQueryStatus, dispatch]);

  useEffect(() => {
    if (cognitoAuthStatus === 'unauthenticated') {
      dispatch(api.util.resetApiState());
      Sentry.setUser(null);
    }
  }, [cognitoAuthStatus, dispatch]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: String(user.id) });
    }
  }, [user]);

  const isAuthenticated =
    cognitoAuthStatus === 'authenticated' &&
    userQueryStatus === QueryStatus.fulfilled;

  return {
    isAuthenticated,
    cognitoAuthStatus,
    userQueryStatus,
    user,
  };
};
